import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, CreditCard, Loader2, AlertTriangle, XCircle, Store, Trash2 } from "lucide-react";

const plans = [
  { name: "Extra Small", slug: "extra-small", limit: 100, prices: { monthly: 45, weekly: 50, daily: 55 } },
  { name: "Small", slug: "small", limit: 1000, prices: { monthly: 75, weekly: 105, daily: 125 } },
  { name: "Medium", slug: "medium", limit: 2500, prices: { monthly: 75, weekly: 95, daily: 130 } },
  { name: "Large", slug: "large", limit: 5000, prices: { monthly: 99, weekly: 159, daily: 275 } },
  { name: "Enterprise", slug: "enterprise", limit: 25000, prices: { monthly: 245, weekly: 395, daily: 1499 } },
  { name: "Extreme", slug: "extreme", limit: 50000, prices: { monthly: 729, weekly: 1049, daily: 2799 } },
];

export default function SubscriptionPage() {
  const { t } = useI18n();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [dbPlans, setDbPlans] = useState([]);
  const [freq, setFreq] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [showChangeOption, setShowChangeOption] = useState(null);

  const freqLabel = (f) => f === "daily" ? t("subscription.scan_daily") : f === "weekly" ? t("subscription.scan_weekly") : t("subscription.scan_monthly");

  useEffect(() => {
    const ps = searchParams.get("payment");
    if (ps === "success") toast.success(t("subscription.payment_success"));
    else if (ps === "cancelled") toast.info(t("subscription.payment_cancelled"));
  }, [searchParams, t]);

  useEffect(() => {
    Promise.all([api.get("/subscriptions/current"), api.get("/subscriptions/plans")])
      .then(([s, p]) => { setSub(s.data); setDbPlans(p.data); if (s.data?.frequency) setFreq(s.data.frequency); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleChangePlan = async (planId, frequency, startImmediately) => {
    setPaying(planId);
    try {
      const res = await api.post("/subscriptions/change", { plan_id: planId, frequency, start_immediately: startImmediately });
      setSub(res.data);
      setShowChangeOption(null);
      if (res.data.invoice_generated) {
        toast.success(`${t("toast.sub_changed")}. ${res.data.amount_charged} EUR`);
      } else if (res.data.scheduled_change) {
        toast.success(t("toast.sub_scheduled"));
      }
      const fresh = await api.get("/subscriptions/current");
      setSub(fresh.data);
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required: continue in the MultiSafepay checkout
        try {
          const order = await api.post("/payments/create-order", { plan_id: planId, frequency, frontend_url: window.location.origin, backend_url: window.location.origin });
          if (order.data.payment_url) { window.location.href = order.data.payment_url; return; }
        } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
      } else {
        toast.error(err.response?.data?.detail || t("common.error"));
      }
    }
    finally { setPaying(null); }
  };

  const handleCancel = async () => {
    try {
      await api.post("/subscriptions/cancel");
      toast.success(t("toast.sub_cancelled"));
      setShowCancel(false);
      const fresh = await api.get("/subscriptions/current");
      setSub(fresh.data);
    } catch { toast.error(t("toast.cancel_failed")); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") { toast.error(t("toast.type_delete")); return; }
    try {
      await api.post("/subscriptions/delete-account", { confirm: "DELETE" });
      toast.success(t("toast.account_deleted"));
      logout();
    } catch { toast.error(t("toast.delete_failed")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const allPlans = dbPlans.length > 0 ? dbPlans : plans;
  const features = [
    t("subscription.feature_monitoring"),
    t("subscription.feature_matching"),
    t("subscription.feature_repricing"),
    t("subscription.feature_shops"),
  ];

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <h1 className="text-2xl font-bold">{t("subscription.title")}</h1>

      {sub && (
        <Card className="bg-card border-border border-l-4 border-l-blue-500" data-testid="current-sub-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t("subscription.current_plan")}</p>
                <p className="text-xl font-bold mt-1">{sub.plan_name}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <Badge variant={sub.status === "trial" ? "secondary" : sub.status === "active" ? "default" : "destructive"} className={sub.status === "active" ? "bg-emerald-500/20 text-emerald-400" : ""}>
                    {sub.status === "trial" ? t("subscription.trial") : sub.status === "active" ? t("subscription.active") : sub.status === "cancelled" ? t("subscription.cancelled") : sub.status}
                  </Badge>
                  <Badge variant="outline" className="capitalize">{t("subscription.scan_label")} {freqLabel(sub.frequency)}</Badge>
                  <Badge variant="outline">{t("subscription.billing_monthly")}</Badge>
                  <Badge variant="outline"><Store className="w-3 h-3 mr-1" />{t("subscription.unlimited_shops")}</Badge>
                </div>
                {sub.pending_plan_name && (
                  <p className="text-sm text-amber-500 mt-2">{t("subscription.scheduled_change")} {sub.pending_plan_name} ({sub.pending_frequency})</p>
                )}
              </div>
              <div className="text-right space-y-2">
                {sub.renewal_date && sub.status !== "cancelled" && (
                  <div><p className="text-xs text-muted-foreground">{t("subscription.next_renewal")}</p><p className="font-mono-data text-sm">{new Date(sub.renewal_date).toLocaleDateString("nl-NL")}</p></div>
                )}
                {sub.total_incl_vat && <p className="font-mono-data text-lg font-bold">{sub.total_incl_vat.toFixed(2)} EUR <span className="text-xs text-muted-foreground">{t("subscription.incl_vat")}</span></p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-center">
        <div className="inline-flex bg-muted rounded-xl p-1.5" data-testid="sub-freq-toggle">
          {["monthly", "weekly", "daily"].map((f) => (
            <button key={f} onClick={() => setFreq(f)} className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${freq === f ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid={`sub-freq-${f}`}>
              {t(`pricing.${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {allPlans.map((plan) => {
          // A cancelled plan can be chosen again to reactivate it
          const isCurrent = sub?.plan_name === plan.name && sub?.frequency === freq && sub?.status !== "cancelled";
          const price = plan.prices?.[freq] || 0;
          const vat = Math.round(price * 21) / 100;
          const total = price + vat;
          return (
            <Card key={plan.id || plan.slug} className={`bg-card border-border transition-all ${isCurrent ? "border-primary ring-1 ring-primary/20" : "hover:border-primary/30"}`} data-testid={`sub-plan-${plan.slug || plan.name.toLowerCase().replace(/\s/g, "-")}`}>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{(plan.product_limit || plan.limit)?.toLocaleString()} {t("pricing.products")}</p>
                <p className="text-3xl font-bold font-mono-data mt-4">{price} <span className="text-sm text-muted-foreground font-normal">{t("subscription.excl_vat_month")}</span></p>
                <p className="text-xs text-muted-foreground font-mono-data">{total.toFixed(2)} {t("subscription.incl_vat_month")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("subscription.scan_frequency")} {freqLabel(freq).toLowerCase()}</p>
                <ul className="space-y-2 my-5">
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="w-3.5 h-3.5 text-emerald-500" /> {f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button variant="outline" className="w-full" disabled>{t("pricing.current")}</Button>
                ) : (
                  <Button className="w-full bg-[#2CA8AC] hover:bg-[#248F92]" disabled={paying === plan.id} onClick={() => setShowChangeOption(plan)} data-testid={`choose-plan-${plan.slug || plan.name}`}>
                    {paying === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    {t("pricing.choose")}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showChangeOption && (
        <Dialog open={!!showChangeOption} onOpenChange={() => setShowChangeOption(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("subscription.change_title")} {showChangeOption.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{t("subscription.change_when")}</p>
              <div className="grid grid-cols-1 gap-3">
                <Button variant="outline" className="h-auto p-4 flex flex-col items-start whitespace-normal text-left" onClick={() => handleChangePlan(showChangeOption.id, freq, true)} data-testid="change-immediate">
                  <span className="font-semibold">{t("subscription.start_now")}</span>
                  <span className="text-xs text-muted-foreground">{t("subscription.start_now_desc").replace("{price}", ((showChangeOption.prices?.[freq] || 0) * 1.21).toFixed(2))}</span>
                </Button>
                {/* A cancelled subscription never renews, so a change "at next renewal" would never happen */}
                {sub?.status !== "cancelled" && (
                  <Button variant="outline" className="h-auto p-4 flex flex-col items-start whitespace-normal text-left" onClick={() => handleChangePlan(showChangeOption.id, freq, false)} data-testid="change-at-renewal">
                    <span className="font-semibold">{t("subscription.at_renewal")}</span>
                    <span className="text-xs text-muted-foreground">{t("subscription.at_renewal_desc")}</span>
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        {sub?.status !== "cancelled" && (
          <Button variant="outline" className="text-amber-500 border-amber-500/30" onClick={() => setShowCancel(true)} data-testid="cancel-sub-btn">
            <XCircle className="w-4 h-4 mr-2" /> {t("subscription.cancel_btn")}
          </Button>
        )}
        <Button variant="outline" className="text-destructive border-destructive/30" onClick={() => setShowDelete(true)} data-testid="delete-account-btn">
          <Trash2 className="w-4 h-4 mr-2" /> {t("subscription.delete_btn")}
        </Button>
      </div>

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />{t("subscription.cancel_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("subscription.cancel_desc")}{sub?.current_period_end ? ` (${new Date(sub.current_period_end).toLocaleDateString("nl-NL")})` : ""}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleCancel} data-testid="confirm-cancel-btn">{t("subscription.cancel_confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-destructive" />{t("subscription.delete_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("subscription.delete_permanent")}</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
              <li>{t("subscription.delete_item_account")}</li>
              <li>{t("subscription.delete_item_products")}</li>
              <li>{t("subscription.delete_item_shops")}</li>
              <li>{t("subscription.delete_item_sub")}</li>
            </ul>
            <p className="text-sm font-semibold text-destructive">{t("subscription.delete_irreversible")}</p>
            <div>
              <p className="text-sm mb-1">{t("subscription.delete_type_confirm")}</p>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" className="font-mono-data" data-testid="delete-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDelete(false); setDeleteConfirm(""); }}>{t("common.cancel")}</Button>
            <Button variant="destructive" disabled={deleteConfirm !== "DELETE"} onClick={handleDeleteAccount} data-testid="confirm-delete-btn">{t("subscription.delete_final")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
